

import { Button, Alert, Combobox, FormField, TextInput, NumberInput, SuccessNotice } from "@a2z/ui";

import { useState } from "react";
import { useTranslations } from "@/lib/i18n/intl";
import type { SubscriptionPlanView } from "@/lib/api/types";
import { keys } from "@/lib/i18n/portal";
import type { Locale } from "@/lib/i18n/config";
import { pickLocalized } from "@/lib/i18n/localized";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { useAssignStoreSubscriptionMutation } from "@/modules/stores/services/mutations/useAssignStoreSubscriptionMutation";

type Props = {
  storeId: string;
  plans: SubscriptionPlanView[];
  locale: Locale;
  onSuccess?: () => void;
};

export function StoreSubscriptionForm({
  storeId,
  plans,
  locale,
  onSuccess,
}: Props) {
  const t = useTranslations();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [planId, setPlanId] = useState("");
  const { mutateAsync: assignSubscription } = useAssignStoreSubscriptionMutation();

  const options = [
    { label: t(keys.admin.subscriptionSelectPlaceholder), value: "" },
    ...plans.map((plan) => ({
      value: plan.id,
      label: pickLocalized(locale, plan.nameAr, plan.nameEn),
    })),
  ];

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    setError(null);
    setSuccess(false);
    setIsSubmitting(true);
    const formData = new FormData(form);
    const reason = formData.get("reason");
    if (planId.trim() === "") {
      setError(t(keys.errors.required));
      setIsSubmitting(false);
      return;
    }
    if (typeof reason !== "string" || reason.trim() === "") {
      setError(t(keys.errors.required));
      setIsSubmitting(false);
      return;
    }

    const startDate = formData.get("startDate");
    const overrideAmount = formData.get("overrideAmount");
    const overridePeriodDays = formData.get("overridePeriodDays");
    const parsedOverrideAmount =
      typeof overrideAmount === "string" && overrideAmount.trim() !== ""
        ? Number(overrideAmount)
        : undefined;
    if (
      parsedOverrideAmount !== undefined &&
      (!Number.isFinite(parsedOverrideAmount) || parsedOverrideAmount < 0)
    ) {
      setError(t(keys.admin.subscriptionPaymentAmountInvalid));
      setIsSubmitting(false);
      return;
    }
    const parsedOverridePeriodDays =
      typeof overridePeriodDays === "string" && overridePeriodDays.trim() !== ""
        ? Number(overridePeriodDays)
        : undefined;
    if (
      parsedOverridePeriodDays !== undefined &&
      (!Number.isFinite(parsedOverridePeriodDays) || parsedOverridePeriodDays <= 0)
    ) {
      setError(t(keys.admin.subscriptionPaymentAmountInvalid));
      setIsSubmitting(false);
      return;
    }

    try {
      await assignSubscription({
        storeId,
        planId,
        reason,
        startDate: typeof startDate === "string" && startDate ? startDate : undefined,
        overrideAmount: parsedOverrideAmount,
        overridePeriodDays: parsedOverridePeriodDays,
      });
    } catch (error) {
      if (error instanceof ApiJsonClientError) {
        setError(error.message || t(keys.errors.generic));
      } else {
        setError(t(keys.errors.generic));
      }
      setIsSubmitting(false);
      return;
    }

    form.reset();
    setPlanId("");
    setSuccess(true);
    setIsSubmitting(false);
    onSuccess?.();
  };

  return (
    <form className="grid gap-4" onSubmit={handleSubmit}>
      {error ? <Alert message={error} helperText={t(keys.errors.recovery)} /> : null}
      {success ? <SuccessNotice title={t(keys.common.updateSuccess)} /> : null}
      <FormField label={t(keys.admin.subscriptionSelectLabel)} name="planId" required>
        <Combobox
          value={planId}
          onChange={(value) => setPlanId(value ?? "")}
          options={options}
          placeholder={t(keys.admin.subscriptionSelectPlaceholder)}
          aria-label={t(keys.admin.subscriptionSelectLabel)}
        />
      </FormField>
      <FormField
        label={t(keys.admin.subscriptionStartDateLabel)}
        name="startDate"
        helperText={t(keys.admin.subscriptionStartDateHelper)}
      >
        <TextInput name="startDate" type="date" />
      </FormField>
      <FormField label={t(keys.admin.subscriptionReasonLabel)} name="reason" required>
        <TextInput
          name="reason"
          required
          placeholder={t(keys.placeholders.subscriptionReason)}
        />
      </FormField>
      <div className="grid gap-4 md:grid-cols-2">
        <FormField
          label={t(keys.admin.subscriptionOverrideAmountLabel)}
          name="overrideAmount"
          helperText={t(keys.admin.subscriptionOverrideAmountHelper)}
        >
          <NumberInput name="overrideAmount" placeholder="0" allowDecimal />
        </FormField>
        <FormField
          label={t(keys.admin.subscriptionOverridePeriodLabel)}
          name="overridePeriodDays"
          helperText={t(keys.admin.subscriptionOverridePeriodHelper)}
        >
          <NumberInput name="overridePeriodDays" placeholder="30" />
        </FormField>
      </div>
      <Button
        type="submit"
        disabled={isSubmitting}
        variant="primary" className="h-11 rounded-lg px-4 text-sm font-semibold transition disabled:opacity-60"
      >
        {isSubmitting ? t(keys.common.loading) : t(keys.admin.subscriptionAssignAction)}
      </Button>
    </form>
  );
}
